import { useEffect, useState, useCallback } from "react";
import { Formik, useFormikContext } from "formik";
import { useHistory } from "react-router-dom";
import {
  Box,
  Input,
  Stack,
  Button,
  FormErrorMessage,
  FormControl,
  FormLabel,
  Link,
  Text,
  Checkbox,
  VStack,
  Select,
} from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { localizedRegionsFactory, toRegionOptions, sortCountries } from "utils/regions";
import { SearchableSelectField } from "components/Form";
import {
  fieldLabelLookup,
  buildInitialValues,
  buildSchema,
  parseRequiredFieldsForAddressAutocomplete,
  SELECT_STYLE_VALIDATION_STR_REGEX,
  getSelectStyleOptionsFromValidationStr,
} from "./utils";
import PlacesAutocompleteAddressTextField from "components/Form/PlacesAutocompleteAddressTextField";

const STRIP_WHITE_SPACES_KEYS = ["account_number", "iban"];

const InnerForm = ({
  onSubmit,
  requiredFields,
  isLoading,
  apiFieldLevelErrorCodes,
  regionsAsOptions,
  countryCode: bankAccountCountryCode,
}) => {
  const { formatMessage } = useIntl();
  const {
    setFieldValue,
    setFieldTouched,
    setFieldError,
    validateForm,
    errors,
    touched,
    values,
    isValid,
  } = useFormikContext();
  const fieldLabels = fieldLabelLookup(formatMessage);
  const [parsedFieldsForAutocomplete] = useState(
    parseRequiredFieldsForAddressAutocomplete(requiredFields), // if address conforms to PlacesAutocompleteAddressTextField, this will parse out fields to what we need.  If not, it will be null
  );
  const [showPlacesAutocompleteField, setShowPlacesAutocompleteField] = useState(
    parsedFieldsForAutocomplete !== null,
  );
  const [isResidentialCountryDisabled, setIsResidentialCountryDisabled] = useState(true);

  const processFieldLevelErrorCodes = useCallback(() => {
    if (apiFieldLevelErrorCodes) {
      Object.keys(apiFieldLevelErrorCodes).forEach((key) => {
        const errorMessages = apiFieldLevelErrorCodes[key].join(", ");
        setFieldError(key, errorMessages);
      });
    }
  }, [apiFieldLevelErrorCodes, setFieldError]);

  useEffect(() => {
    processFieldLevelErrorCodes(); // trigger function when dependencies (mainly apiFieldLevelErrorCodes) change
  }, [processFieldLevelErrorCodes]);

  const onPlacesSelection = (selectionData) => {
    const { tilia_address } = selectionData;

    setFieldValue("address", `${tilia_address.street || ""} ${tilia_address.street2 || ""}`);
    setFieldValue("city", tilia_address.city || "");
    setFieldValue("region", tilia_address.state || "");
    setFieldValue("postal_code", tilia_address.zip || "");

    setFieldTouched("address", true);
    setFieldTouched("city", true);
    setFieldTouched("region", true);
    setFieldTouched("postal_code", true);

    setTimeout(validateForm, 500);

    setShowPlacesAutocompleteField(false);
  };

  const onManualAddressEntryClick = () => {
    setShowPlacesAutocompleteField(false);
  };

  const handleIsResidentialCountrySelectionDisabled = useCallback(() => {
    setIsResidentialCountryDisabled(!isResidentialCountryDisabled)
    if (!isResidentialCountryDisabled && values.country !== bankAccountCountryCode) {
      setFieldValue("country", bankAccountCountryCode);
      // clean any address fields if set
      values.address && setFieldValue("address", "");
      values.city && setFieldValue("city", "");
      values.region && setFieldValue("region", "");
      values.postal_code && setFieldValue("postal_code", "");
    }
  }, [values, isResidentialCountryDisabled, setIsResidentialCountryDisabled, setFieldValue, bankAccountCountryCode]);

  const CountryField = (f) => {
    return (
      <VStack key="country-field" align="flex-end">
        <SearchableSelectField
          key={`form-input-${f.request_key}`}
          label={formatMessage({
            defaultMessage: "Country of residence",
            description: "Selection input label",
          })}
          name="country"
          options={regionsAsOptions}
          menuPortalTarget={document.body}
          placeholder={formatMessage({
            defaultMessage: "Select...",
            description: "select placeholder",
          })}
          isDisabled={isResidentialCountryDisabled}
        />
        <Checkbox
          size="sm"
          isChecked={!isResidentialCountryDisabled}
          onChange={handleIsResidentialCountrySelectionDisabled}
        >
          <Text fontSize="xs" color="secondaryText">
            <FormattedMessage
              defaultMessage="My bank account is in a different country than where I live"
              description="Checkbox input description"
            />
          </Text>
        </Checkbox>
      </VStack>
    );
  };

  const SelectField = (f, selectOptions) => {
    const { request_key, label, hidden, type } = f;
    if (hidden) return null;
    const isInvalid = !!(errors[request_key] && touched[request_key]);
    const key = `form-input-${request_key}`;
    return (
      <Box key={key}>
        <FormControl isInvalid={isInvalid}>
          <FormLabel htmlFor={key} requiredIndicator={true}>
            {fieldLabels[type] || label}
          </FormLabel>
          <Select
            value={values[request_key]}
            data-testid={`select-for-${request_key}`}
            onChange={(e) => {
              let val = e.target.value;
              setFieldValue(request_key, val);
            }}
          >
            {selectOptions.map((option, index) => <option key={`option-${index}`} value={option}>{option}</option>)}
          </Select>
          {isInvalid && <FormErrorMessage>{errors[request_key]}</FormErrorMessage>}
        </FormControl>
      </Box>
    );
  };

  const RequiredField = (f) => {
    const { request_key, label, hidden, type, expected_value } = f;
    if (hidden) return null;
    const isInvalid = !!(errors[request_key] && touched[request_key]);
    const key = `form-input-${request_key}`;
    return (
      <Box key={key}>
        <FormControl isInvalid={isInvalid}>
          <FormLabel htmlFor={key} requiredIndicator={true}>
            {fieldLabels[type] || label}
          </FormLabel>
          <Input
            name={request_key}
            id={key}
            role={key}
            value={values[request_key]}
            placeholder={expected_value || ""}
            onChange={(e) => {
              let val = e.target.value;
              if (STRIP_WHITE_SPACES_KEYS.includes(request_key)) {
                // white spaces can get in the way of some validations (like account numbers), so allowing some defined fields to have whitespaces removed
                val = val.replaceAll(" ", "");
              } else {
                // we want to remove all whitespaces at the begining of input values by default
                val = val.replace(/^\s+/g, "");
              }
              setFieldValue(request_key, val);
            }}
            onBlur={() => {
              setFieldTouched(request_key, true);
            }}
          />
          {isInvalid && <FormErrorMessage>{errors[request_key]}</FormErrorMessage>}
        </FormControl>
      </Box>
    );
  };

  const renderNonAutocompleteFieldsOrAllWhenApplicable = () => {
    return (parsedFieldsForAutocomplete?.otherFields || requiredFields).map((f) => {
      // if we have 'otherFields' render those here, otherwise render all fields
      if (f.type === "Country") {
        return CountryField(f);
      } else if (SELECT_STYLE_VALIDATION_STR_REGEX.test(f.validation)) {
        const selectOptions = getSelectStyleOptionsFromValidationStr(f.validation);
        if (Array.isArray(selectOptions) && selectOptions.length > 0) {
          return SelectField(f, selectOptions);
        } else {
          return RequiredField(f); // default to required field if not everything matches expectations
        }
      }
      return RequiredField(f);
    });
  };

  const renderJustCountryFieldIfApplicable = () => {
    return (
      parsedFieldsForAutocomplete?.countryField &&
      CountryField(parsedFieldsForAutocomplete.countryField)
    );
  };

  const renderPlacesAutocompleteOrAddressFieldsIfApplicable = () => {
    // PlacesAutocompleteAddressTextField will only be shown once, either the user
    // will make an autocomplete selection, which then renders the underlying fields in the
    // form with the values we understand, or the user will select 'Enter address manually'.
    // Once removed in the flow, the PlacesAutocompleteAddressTextField field will not be shown
    // again
    if (!parsedFieldsForAutocomplete) return null;
    if (showPlacesAutocompleteField) {
      return (
        <>
          <PlacesAutocompleteAddressTextField
            label={formatMessage({
              defaultMessage: "Residential address",
              description: "Input label",
            })}
            placeholder={formatMessage({
              defaultMessage: "Enter address",
              description: "input placeholder",
            })}
            name="googlePlacesAutocomplete"
            onUserSelection={(selectionData) => {
              onPlacesSelection(selectionData);
            }}
            country={values.country || "US"}
            isDisabled={false}
            onBlur={() => {
              setTimeout(() => {
                if (showPlacesAutocompleteField) {
                  // this is to get around an autofill issue that won't trigger Google Places Autocomplete
                  // as a fallback, let's just show the full address form when bluring (if it's not already being shown)
                  setShowPlacesAutocompleteField(false);
                }
              }, 500);
            }}
          />
          <Link
            data-testid="address-autocomplete-manual-mode-button"
            color="integrator.500"
            onClick={onManualAddressEntryClick}
          >
            <FormattedMessage
              defaultMessage="Enter address manually"
              description="Link/button text"
            />
          </Link>
        </>
      );
    } else {
      return (parsedFieldsForAutocomplete?.addressFields || []).map((f) => RequiredField(f));
    }
  };

  return (
    <Stack spacing="4">
      {renderNonAutocompleteFieldsOrAllWhenApplicable()}
      {renderJustCountryFieldIfApplicable()}
      {renderPlacesAutocompleteOrAddressFieldsIfApplicable()}
      <Button
        mt="8"
        width="100%"
        isLoading={isLoading}
        onClick={() => {
          onSubmit && onSubmit(values);
        }}
        isDisabled={!isValid || isLoading}
      >
        <FormattedMessage defaultMessage="Save Bank Account" description="Button label" />
      </Button>
    </Stack>
  );
};

const FormComponent = (props) => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const { countryCode, useAddressOnlyDescription } = props;
  const initialValues = buildInitialValues(props.requiredFields, countryCode);
  const schema = buildSchema(props.requiredFields, formatMessage);
  const regions = sortCountries(localizedRegionsFactory(formatMessage));
  const [bankCountryDetails] = useState(regions.find((r) => r.ISO === countryCode)); // currently selected bank country details
  const regionsAsOptions = toRegionOptions(regions);

  const fullFormDescription = (
    <FormattedMessage
      defaultMessage="Enter a bank account located in {countryName} below. If your bank account is in another country, <link>click here</link> to go back and change the selected country."
      description="Description of what information is needed from the user in the presented form. {countryName} is a value that will get replaced with an already translated string."
      values={{
        countryName: (
          <Text as="i" color="integrator.500">
            {bankCountryDetails?.Name || countryCode}
          </Text>
        ),
        link: (txt) => (
          <Button onClick={() => history.goBack()} variant="link">
            {txt}
          </Button>
        ),
      }}
    />
  );

  const addressOnlyDescription = (
    <FormattedMessage
      defaultMessage="Please enter your residential address information associated with the bank account you added (located in {countryName}) below."
      description="Description "
      values={{
        countryName: (
          <Text as="i" color="integrator.500">
            {bankCountryDetails?.Name || countryCode}
          </Text>
        ),
        link: (txt) => (
          <Button onClick={() => history.goBack()} variant="link">
            {txt}
          </Button>
        ),
      }}
    />
  );

  return (
    <>
      <Box pt="2" pb="8" data-testid="bank-account-form-directions">
        {useAddressOnlyDescription ? addressOnlyDescription : fullFormDescription}
      </Box>
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={() => {}}
        validateOnMount
      >
        <InnerForm {...props} regionsAsOptions={regionsAsOptions} />
      </Formik>
    </>
  );
};

export default FormComponent;
